import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    CAlert,
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CSpinner
} from '@coreui/react';
import { getApi } from '../../apis/backendApis';
import '../../scss/styles-infra.scss';

/** Uma loja que já existe numa base. */
interface LojaDaBase {
    slug: string;
    loja: number;
    dominio: string;
}

/** Uma base de cliente, com as lojas que vivem dentro dela. */
interface BaseDeCliente {
    banco: string;
    farm: string;
    host: string;
    /** A loja de menor id: é ela que roda as migrations desta base. */
    principal: number;
    lojas: Array<LojaDaBase>;
}

/**
 * Abre a esteira de uma loja a mais numa base que já existe.
 *
 * A ordem dos campos é a ordem da decisão: farm, base, loja de origem, e só
 * então os dados da loja nova. Escolher a base antes da loja não é detalhe de
 * layout: é ela que define a farm, os buckets e quais lojas existem para copiar.
 */
const LojaForm = () => {
    const history = useHistory();

    const [bases, setBases] = useState<Array<BaseDeCliente>>([]);
    const [farms, setFarms] = useState<Array<string>>([]);
    const [carregando, setCarregando] = useState<boolean>(true);
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState<boolean>(false);

    const [farm, setFarm] = useState<string>('');
    const [banco, setBanco] = useState<string>('');
    const [origem, setOrigem] = useState<string>('');
    const [nome, setNome] = useState<string>('');
    const [dominio, setDominio] = useState<string>('');
    const [slug, setSlug] = useState<string>('');

    const carregar = useCallback(() => {
        setCarregando(true);
        getApi().get('/infra/loja/bases')
            .then(resposta => {
                setBases(resposta.data.bases || []);
                setFarms(resposta.data.farms || []);
                setErro(null);
            })
            .catch(motivo => setErro(
                motivo?.response?.data?.error || 'Não consegui listar as bases.'))
            .finally(() => setCarregando(false));
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    const daFarm = bases.filter(base => !farm || base.farm === farm);
    const baseEscolhida = bases.filter(base => base.banco === banco)[0] || null;

    // Trocar de farm invalida a base, e trocar de base invalida a loja: manter
    // a escolha anterior deixaria o formulário dizendo uma coisa e mandando
    // outra.
    const trocarFarm = (valor: string) => {
        setFarm(valor);
        setBanco('');
        setOrigem('');
    };

    const trocarBanco = (valor: string) => {
        setBanco(valor);
        const base = bases.filter(b => b.banco === valor)[0];
        setOrigem(base ? String(base.principal) : '');
    };

    // O slug sai do nome quando ninguém digitou um, no mesmo formato dos que já
    // existem: minúsculas, tudo junto.
    const slugEfetivo = slug.trim() || nome.toLowerCase().replace(/[^a-z0-9]/g, '');

    const podeCriar = !!(farm && banco && origem && nome.trim() && dominio.trim());

    const criar = () => {
        if (!podeCriar || salvando || !baseEscolhida) {
            return;
        }
        setSalvando(true);
        setErro(null);

        getApi().post('/infra/loja/create', {
            nome: nome.trim(),
            dominio: dominio.trim(),
            slug: slugEfetivo,
            farm,
            database: banco,
            loja_origem: Number(origem),
            loja_principal: baseEscolhida.principal
        })
            .then(resposta => history.push('/infra/pipeline/' + resposta.data.id))
            .catch(motivo => {
                setErro(motivo?.response?.data?.error || 'Não consegui criar a esteira.');
                setSalvando(false);
            });
    };

    return (
        <div className="infra-container infra-container-rolavel">
            <div className="infra-cabecalho">
                <div>
                    <h4 className="infra-titulo">Nova loja em cliente existente</h4>
                    <div className="infra-subtitulo">
                        <span>Mais uma loja dentro de uma base que já está de pé</span>
                    </div>
                </div>
                <div className="infra-acoes-topo">
                    <CButton color="secondary" variant="outline" onClick={() => history.push('/infra')}>
                        Voltar
                    </CButton>
                    <CButton color="primary" onClick={criar} disabled={!podeCriar || salvando}>
                        {salvando ? 'Criando…' : 'Criar esteira'}
                    </CButton>
                </div>
            </div>

            {erro && <CAlert color="danger">{erro}</CAlert>}

            {carregando && <div className="infra-carregando"><CSpinner color="primary" /></div>}

            {!carregando && (
                <CCard>
                    <CCardHeader className="infra-card-header">
                        <strong>Onde a loja entra</strong>
                    </CCardHeader>
                    <CCardBody>
                        <div className="infra-loja-campos">
                            <label>
                                <span>Farm</span>
                                <select value={farm} onChange={e => trocarFarm(e.target.value)}>
                                    <option value="">escolha</option>
                                    {farms.map(f => <option key={f} value={f}>{f}</option>)}
                                </select>
                            </label>

                            <label>
                                <span>Base do cliente</span>
                                <select
                                    value={banco}
                                    onChange={e => trocarBanco(e.target.value)}
                                    disabled={!farm}
                                >
                                    <option value="">escolha</option>
                                    {daFarm.map(base => (
                                        <option key={base.banco} value={base.banco}>
                                            {base.banco} ({base.lojas.length} loja
                                            {base.lojas.length > 1 ? 's' : ''})
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label>
                                <span>Loja de origem da cópia</span>
                                <select
                                    value={origem}
                                    onChange={e => setOrigem(e.target.value)}
                                    disabled={!baseEscolhida}
                                >
                                    <option value="">escolha</option>
                                    {(baseEscolhida?.lojas || []).map(loja => (
                                        <option key={loja.loja} value={loja.loja}>
                                            {loja.loja} · {loja.slug}
                                            {loja.loja === baseEscolhida?.principal ? ' (principal)' : ''}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        {baseEscolhida && (
                            <div className="infra-loja-resumo">
                                <span>farm <strong>{baseEscolhida.farm}</strong></span>
                                <span>banco <strong>{baseEscolhida.banco}</strong></span>
                                <span>
                                    buckets <strong>{baseEscolhida.banco}.img</strong> e{' '}
                                    <strong>{baseEscolhida.banco}.fiscal</strong>, os mesmos do cliente
                                </span>
                                <span>
                                    quem roda as migrations desta base: loja{' '}
                                    <strong>{baseEscolhida.principal}</strong>
                                </span>
                            </div>
                        )}
                    </CCardBody>
                </CCard>
            )}

            {!carregando && (
                <CCard>
                    <CCardHeader className="infra-card-header">
                        <strong>A loja nova</strong>
                    </CCardHeader>
                    <CCardBody>
                        <div className="infra-loja-campos">
                            <label>
                                <span>Nome</span>
                                <input
                                    value={nome}
                                    onChange={e => setNome(e.target.value)}
                                    placeholder="Andri Kids"
                                />
                            </label>

                            <label>
                                <span>Domínio</span>
                                <input
                                    value={dominio}
                                    onChange={e => setDominio(e.target.value)}
                                    placeholder="andrikids.com.br"
                                />
                            </label>

                            <label>
                                <span>Slug</span>
                                <input
                                    value={slug}
                                    onChange={e => setSlug(e.target.value)}
                                    placeholder={slugEfetivo || 'sai do nome'}
                                />
                            </label>
                        </div>

                        <div className="infra-loja-resumo">
                            <span>
                                O id da loja não se escolhe aqui: ele sai do próximo livre no
                                Loja.php, e a validação recusa se ele for menor que o da loja
                                principal da base.
                            </span>
                        </div>
                    </CCardBody>
                </CCard>
            )}
        </div>
    );
};

export default LojaForm;
